import { useEffect, useState } from "react";
import { logger } from "../lib/logger";
import { toast } from "sonner";
import {
  AlertCircle, Hash, Image as ImageIcon, Loader2, MessageSquare, Sparkles, Zap,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { AppShell } from "../components/AppShell";
import { CopyBtn } from "../components/CopyBtn";
import { api, BACKEND_URL, formatError } from "../lib/api";

const TABS = [
  { id: "hashtags", label: "Hashtags", icon: Hash, cost: "1 cr" },
  { id: "captions", label: "Captions", icon: MessageSquare, cost: "1 cr" },
  { id: "thumbnails", label: "Thumbnails", icon: ImageIcon, cost: "3 cr" },
];

const PLATFORMS = ["tiktok", "instagram", "youtube", "twitter", "linkedin"];

function HashtagsTab() {
  const { refreshUser } = useAuth();
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState("tiktok");
  const [count, setCount] = useState(20);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const go = async (e) => {
    e.preventDefault();
    setErr("");
    setResult(null);
    setLoading(true);
    try {
      const { data } = await api.post("/social/hashtags", { topic, platform, count });
      setResult(data);
      toast.success(`${data.hashtags.length} hashtags generated`);
      await refreshUser();
    } catch (error) {
      logger.error("Hashtag gen error", error);
      const msg = formatError(error.response?.data?.detail) || "Failed";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const combined = result?.hashtags?.join(" ") || "";

  return (
    <form onSubmit={go} className="space-y-5" data-testid="hashtags-form">
      <div>
        <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-1">Topic or niche</label>
        <input value={topic} onChange={(e) => setTopic(e.target.value)} required maxLength={200}
          className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-zinc-900 dark:text-white focus:border-brand focus:ring-1 focus:ring-brand outline-none"
          placeholder="e.g., home workout tips for beginners" data-testid="hashtag-topic-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-1">Platform</label>
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-sm text-zinc-900 dark:text-white outline-none" data-testid="hashtag-platform-select">
            {PLATFORMS.map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-1">How many</label>
          <select value={count} onChange={(e) => setCount(Number(e.target.value))} className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-sm text-zinc-900 dark:text-white outline-none" data-testid="hashtag-count-select">
            {[10, 20, 30].map((n) => <option key={n} value={n}>{n} hashtags</option>)}
          </select>
        </div>
      </div>
      {err && (
        <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm p-3 flex items-center gap-2" data-testid="hashtag-error">
          <AlertCircle className="w-4 h-4" />{err}
        </div>
      )}
      <button type="submit" disabled={loading} data-testid="hashtag-generate-btn" className="w-full bg-brand text-white font-semibold py-3 hover:bg-brand-hover flex items-center justify-center gap-2 disabled:opacity-50">
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Researching trends...</> : <><Hash className="w-4 h-4" /> Generate Hashtags (1 cr)</>}
      </button>
      {result && (
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5 animate-fade-in" data-testid="hashtag-result">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold tracking-[0.15em] text-brand uppercase">{result.hashtags.length} hashtags</span>
            <CopyBtn text={combined} label="Copy All" />
          </div>
          <div className="flex flex-wrap gap-2">
            {result.hashtags.map((h) => (
              <span key={h} className="text-sm font-medium bg-brand/5 text-brand px-3 py-1.5 border border-brand/20" data-testid={`hashtag-${h}`}>{h}</span>
            ))}
          </div>
        </div>
      )}
    </form>
  );
}

function CaptionsTab() {
  const { refreshUser } = useAuth();
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [variants, setVariants] = useState(5);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const go = async (e) => {
    e.preventDefault();
    setErr("");
    setResult(null);
    setLoading(true);
    try {
      const { data } = await api.post("/social/captions", { topic, platform, variants });
      setResult(data);
      toast.success(`${data.captions.length} captions generated`);
      await refreshUser();
    } catch (error) {
      logger.error("Caption gen error", error);
      const msg = formatError(error.response?.data?.detail) || "Failed";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={go} className="space-y-5" data-testid="captions-form">
      <div>
        <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-1">What's the post about?</label>
        <textarea value={topic} onChange={(e) => setTopic(e.target.value)} required rows={3} maxLength={400}
          className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-zinc-900 dark:text-white focus:border-brand focus:ring-1 focus:ring-brand outline-none resize-none"
          placeholder="e.g., My new vegan recipe book launch — 30 easy dinners" data-testid="caption-topic-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-1">Platform</label>
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-sm text-zinc-900 dark:text-white outline-none" data-testid="caption-platform-select">
            {PLATFORMS.map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-1">Variants</label>
          <select value={variants} onChange={(e) => setVariants(Number(e.target.value))} className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-sm text-zinc-900 dark:text-white outline-none" data-testid="caption-variants-select">
            {[3, 5, 10].map((n) => <option key={n} value={n}>{n} captions</option>)}
          </select>
        </div>
      </div>
      {err && (
        <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm p-3 flex items-center gap-2" data-testid="caption-error">
          <AlertCircle className="w-4 h-4" />{err}
        </div>
      )}
      <button type="submit" disabled={loading} data-testid="caption-generate-btn" className="w-full bg-brand text-white font-semibold py-3 hover:bg-brand-hover flex items-center justify-center gap-2 disabled:opacity-50">
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Writing A/B hooks...</> : <><MessageSquare className="w-4 h-4" /> Generate Captions (1 cr)</>}
      </button>
      {result && (
        <div className="space-y-3 animate-fade-in" data-testid="caption-results">
          {result.captions.map((c, i) => (
            <div key={c.slice(0, 40) + i} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4" data-testid={`caption-variant-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold tracking-wider text-brand uppercase">Variant {i + 1}</span>
                <CopyBtn text={c} />
              </div>
              <p className="text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap leading-relaxed">{c}</p>
            </div>
          ))}
        </div>
      )}
    </form>
  );
}

const THUMB_STYLES = ["bold-youtube", "clean-instagram", "edgy-tiktok", "corporate", "playful"];

function ThumbnailsTab() {
  const { refreshUser } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("bold-youtube");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/social/thumbnails");
      setHistory(data);
    } catch (error) {
      logger.error("Failed to load thumbnail history", error);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const go = async (e) => {
    e.preventDefault();
    setErr("");
    setResult(null);
    setLoading(true);
    try {
      const { data } = await api.post("/social/thumbnail", { prompt, style });
      setResult(data);
      toast.success("Thumbnail generated!");
      await refreshUser();
      loadHistory();
    } catch (error) {
      logger.error("Thumbnail gen error", error);
      const msg = formatError(error.response?.data?.detail) || "Failed";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={go} className="space-y-5" data-testid="thumbnails-form">
        <div>
          <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-1">Describe your thumbnail</label>
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} required rows={3} maxLength={400}
            className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-zinc-900 dark:text-white focus:border-brand focus:ring-1 focus:ring-brand outline-none resize-none"
            placeholder='e.g., Bold YouTube thumbnail: shocked face, "I tried this for 30 days" text, red-yellow background'
            data-testid="thumbnail-prompt-input" />
        </div>
        <div>
          <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-1">Style</label>
          <select value={style} onChange={(e) => setStyle(e.target.value)} className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-sm text-zinc-900 dark:text-white outline-none" data-testid="thumbnail-style-select">
            {THUMB_STYLES.map((s) => <option key={s} value={s}>{s.replace("-", " ")}</option>)}
          </select>
        </div>
        <div className="bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 p-4 flex items-center gap-2">
          <Zap className="w-4 h-4 text-brand" />
          <span className="text-sm font-bold text-zinc-900 dark:text-white">3 credits</span>
          <span className="text-xs text-zinc-500 ml-auto">Powered by GPT Image 1</span>
        </div>
        {err && (
          <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm p-3 flex items-center gap-2" data-testid="thumbnail-error">
            <AlertCircle className="w-4 h-4" />{err}
          </div>
        )}
        <button type="submit" disabled={loading} data-testid="thumbnail-generate-btn" className="w-full bg-brand text-white font-semibold py-3 hover:bg-brand-hover flex items-center justify-center gap-2 disabled:opacity-50">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Rendering thumbnail... (30-60s)</> : <><Sparkles className="w-4 h-4" /> Generate Thumbnail</>}
        </button>
      </form>

      {result && (
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4 animate-fade-in" data-testid="thumbnail-result">
          <img src={`${BACKEND_URL}${result.image_url}`} alt="Generated thumbnail" className="w-full max-w-2xl mx-auto" data-testid="thumbnail-image" />
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-zinc-400">{result.style} · {result.credits_used} cr used</span>
            <a href={`${BACKEND_URL}${result.image_url}`} download className="text-sm font-semibold text-brand hover:underline" data-testid="thumbnail-download">Download PNG</a>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div data-testid="thumbnail-history">
          <h3 className="text-lg font-bold text-zinc-900 dark:text-white mb-3 font-heading">Your thumbnails</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {history.map((t) => (
              <div key={t.id} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 overflow-hidden">
                <img src={`${BACKEND_URL}/api/social/thumbnail/file/${t.id}`} alt={t.prompt} className="w-full aspect-square object-cover" />
                <div className="p-3">
                  <p className="text-xs text-zinc-500 truncate">{t.prompt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SocialToolsPage() {
  const [tab, setTab] = useState("hashtags");

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8" data-testid="social-tools-page">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
            <Hash className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-black tracking-tighter text-zinc-950 dark:text-white font-heading">Social Media Toolkit</h1>
            <p className="text-sm text-zinc-500">Viral hashtags, A/B captions, and scroll-stopping thumbnails</p>
          </div>
        </div>

        <div className="flex gap-1 mb-6 border-b border-zinc-200 dark:border-zinc-800 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`social-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-px whitespace-nowrap ${tab === t.id ? "border-brand text-brand" : "border-transparent text-zinc-500 hover:text-zinc-900 dark:hover:text-white"}`}>
              <t.icon className="w-4 h-4" />
              {t.label}
              <span className="text-[10px] font-bold text-zinc-400 ml-1">{t.cost}</span>
            </button>
          ))}
        </div>

        {tab === "hashtags" && <HashtagsTab />}
        {tab === "captions" && <CaptionsTab />}
        {tab === "thumbnails" && <ThumbnailsTab />}
      </div>
    </AppShell>
  );
}
